import React, { useMemo } from 'react';
import styled from 'styled-components';
import { CheckIndicator, CheckIndicatorStatus } from '@sdir/sds';
import { SimulatorResultItem } from '@sdir/httpclient/lib/clients/aps/application';
import { QualificationActions } from '../QualificationActions';
import { AddedQualifications } from '../../../types';
import {
  attachmentIsValid,
  qualificationIsApproved,
  qualificationIsComplete,
  qualificationIsRejected,
} from '../../../helpers/Qualifications';
import { useCurrentUserInfo } from '../../../services/hooks/useCurrentUserInfo';

export interface DescriptionBoxInterface {
  qualification: SimulatorResultItem;
  addedQualifications: AddedQualifications;
  showValidation?: boolean;
  isNewApplication?: boolean;
  externalStatus?: CheckIndicatorStatus;
  cardStatus?: QualificationCardStatus;
}

export enum QualificationCardStatus {
  None = 'none',
  Ok = 'ok',
  Valid = 'valid',
  Warning = 'warning',
  Error = 'error',
}

export const determineQualificationCardStatus = (
  isNewApplication,
  qualification,
  addedQualifications,
  attachments,
  confirmations
) => {
  const isComplete = qualificationIsComplete(
    qualification,
    addedQualifications,
    attachments,
    confirmations
  );
  const isApproved = qualificationIsApproved(
    qualification,
    addedQualifications,
    attachments,
    confirmations
  );
  const isRejected = qualificationIsRejected(qualification, addedQualifications, attachments);
  const attachmentsForQualification =
    attachments?.filter((a) => a.simulatorResultItemUri === qualification.uri) || [];
  const attachment =
    attachmentsForQualification?.length > 0 &&
    attachmentsForQualification[attachmentsForQualification?.length - 1];
  const hasValidAddedInfo =
    !!attachment && attachmentIsValid(attachment, addedQualifications, true);
  if (isApproved) return QualificationCardStatus.Valid;
  if (isNewApplication) {
    if (isComplete) return QualificationCardStatus.Valid;
  } else if (isComplete && !isRejected) {
    // On MissingDocumentationPage. If the info is added and not rejected, it is shown as valid.
    return QualificationCardStatus.Valid;
  }
  if (attachmentsForQualification.length > 0 && !hasValidAddedInfo) {
    return QualificationCardStatus.Error;
  }
  return QualificationCardStatus.Warning;
};

const QualificationBaseCard: React.FC<DescriptionBoxInterface> = ({
  qualification,
  addedQualifications,
  externalStatus,
  cardStatus = QualificationCardStatus.None,
}) => {
  const {
    userInfo: { isExternal },
  } = useCurrentUserInfo();

  const indicatorStatus = useMemo(() => {
    if (externalStatus) return externalStatus;
    if (cardStatus === QualificationCardStatus.None) return null;
    return cardStatus;
  }, [cardStatus, externalStatus]);

  if (qualification?.status === true) return <></>;

  return (
    <BodyFrame
      className={`status--${cardStatus}`}
      data-testid={`base-card-${qualification?.title}`}
    >
      <QualificationBaseHeader qualification={qualification} status={indicatorStatus} hasActions />
      <ActionsDiv>
        <QualificationActions
          qualification={qualification}
          addedQualifications={addedQualifications}
          qualificationStatus={cardStatus}
          qualificationKey="base"
          isExternalUser={isExternal}
        />
      </ActionsDiv>
    </BodyFrame>
  );
};

export default QualificationBaseCard;

export const QualificationBaseHeader = ({ qualification, status, hasActions = false }) => {
  return (
    <Header>
      <CheckIndicator status={status} />
      <Description className={hasActions ? 'has-actions' : ''}>
        {qualification.title ? <strong>{qualification.title}: </strong> : <></>}
        <div dangerouslySetInnerHTML={{ __html: qualification.description || '' }} />
      </Description>
    </Header>
  );
};

export const BodyFrame = styled.article`
  padding: 3rem;
  justify-content: center;
  align-items: center;
  height: auto;
  box-shadow: ${({ theme }) => theme.card.boxShadow};
  border-radius: ${({ theme }) => theme.card.borderRadius};
  background-color: ${({ theme }) => theme.colors.background.secondary};
  transition: 250ms ease;
  position: relative;

  &.status {
    &--error {
      border: 1px solid ${({ theme }) => theme.colors.alert.errorDark};
      background-color: ${({ theme }) => theme.colors.alert.errorLight};
    }
    &--warning {
      border: 1px solid ${({ theme }) => theme.colors.alert.warningDark};
      background-color: ${({ theme }) => theme.colors.alert.warningLight};
    }
    &--valid {
      border: 1px solid ${({ theme }) => theme.colors.alert.successDark};
      background-color: ${({ theme }) => theme.colors.alert.successLight};
    }
  }

  &.signature,
  &.photo {
    max-width: 500px;
    h2,
    p {
      display: none;
    }
    div:last-child {
      margin-left: 0;
    }
  }
`;

const Header = styled.div`
  display: flex;

  & > span:first-child {
    min-width: 4rem;
  }
`;
const Description = styled.div`
  line-height: 1.5;
  margin-left: 15px;

  &.has-actions {
    min-height: 100px;
  }

  & > div {
    display: inline;
    margin-left: 5px;
  }
`;
const ActionsDiv = styled.div`
  bottom: 20px;
  bottom: 120px;
  margin-top: 16px;
`;
